import { useMemo, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Button, Modal, Column, LabeledSelect } from 'components/Blocks';
import { DragItem, DragList, DropResult } from 'components/DragList';

import { DragHorizontalIcon } from 'components/Icons';

import { FormControlDispatchAction } from 'hooks';

import { ListSorters, ManipulationState } from './types';
import { cn } from 'styles';

interface SortProjectsProps<Entity> {
  isOpen: boolean;
  close: () => void;
  dispatch: FormControlDispatchAction<ManipulationState>;
  activeSorters: string[];
  itens: Entity[];
  sorters: ListSorters<Entity>[];
}

function SortModal<E>({
  isOpen,
  close,
  activeSorters,
  dispatch,
  sorters,
}: SortProjectsProps<E>): JSX.Element {
  const [localRules, setLocalRules] = useState(activeSorters);

  const { t } = useLang();

  const handleApplySort = (): void => {
    const hasSort = !!localRules.length;

    const actualRules = hasSort ? localRules : [];

    dispatch({ sorters: actualRules });

    close();

    if (!hasSort) return setLocalRules(actualRules);
  };

  const defaultRules = sorters.filter((sort) => sort.default).map(({ id }) => id);

  const handleSelectChange = (newRules: string[]): void => {
    setLocalRules((old) => {
      const isRemoval = newRules.length < old.length;

      if (!isRemoval) return [...old, newRules.find((rule) => !old.includes(rule)) as string];

      const remaining = old.filter((rule) => newRules.includes(rule));

      return remaining.length ? remaining : defaultRules;
    });
  };

  const reorder = (startIndex: number, endIndex: number): void => {
    const newOrder = [...localRules];

    const [removed] = newOrder.splice(startIndex, 1);
    newOrder.splice(endIndex, 0, removed);

    setLocalRules(newOrder);
  };

  const onDragEnd = (result: DropResult): void => {
    const { destination, source } = result;

    if (!destination) return;

    reorder(source.index, destination.index);
  };

  const options = useMemo(() => {
    return sorters.map(({ getLabel, id }) => ({ id, label: getLabel(t) }));
  }, [sorters, t]);

  return (
    <Modal
      width="min(80vw, 550px)"
      isOpen={isOpen}
      close={close}
      title={t('build-your-sorting-rule') as string}
    >
      <Column gap="8">
        <LabeledSelect
          options={options}
          value={localRules}
          onChangeDirect={handleSelectChange}
          placeholder={t('select-your-sort-rules') as string}
          label={t('sort-rules') as string}
          isMulti
        />

        {localRules.length > 1 && (
          <Column gap="2">
            <p className="font-semibold">{t('select-the-priority-of-your-rules')}</p>

            <DragList
              onDragEnd={onDragEnd}
              droppableId="droppable"
              style={{ minHeight: 40 * localRules.length }}
            >
              {localRules.map((rule, index) => (
                <DragItem
                  key={rule}
                  draggableId={rule}
                  index={index}
                  className={(snapshot) =>
                    cn(
                      'flex cursor-grab items-center gap-1 rounded-lg p-2',
                      snapshot.isDragging && 'bg-hover',
                    )
                  }
                >
                  <DragHorizontalIcon />

                  <span>{sorters.find(({ id }) => id === rule)?.getLabel(t)}</span>
                </DragItem>
              ))}
            </DragList>
          </Column>
        )}

        <Button onClick={handleApplySort}>{t('apply')}</Button>
      </Column>
    </Modal>
  );
}

export default SortModal;
